import React, { useState, useEffect } from 'react';
import { Box, Grid, Skeleton, useMediaQuery, Pagination } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import ListItem from '../../holders_list/components/MemberCard';
import { Homeless, Caseload } from 'types';
// API imports
import * as homelessesApi from '../../../api/homelesses';
import SamaritanFetch from '../../../api/httpClient';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            width: '96%',
            margin: '16px 0'
        },
        empty: {
            fontSize: '1.2rem',
            fontWeight: 600,
            marginLeft: '16px',
            textAlign: 'center',
            color: '#666'
        },
        skeletonContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '96%'
        },
        paginationContainer: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: '32px',
            marginBottom: '8px',
        }
    })
);



//getting user's active caseloads (reverse to show most recent first)
const fetchMemberCaseloads = async (): Promise<Caseload[]> => {
    try {
        const response = await homelessesApi.fetchUserCaseloads();
        if (response && response.ok) {
            const data = await response.json();
            return data.ctp_caseloads.filter((caseload: Caseload) => caseload.status === 'active').reverse() || [];
        }
        throw new Error('Failed to fetch caseloads');
    } catch (error) {
        console.error('Error fetching caseloads:', error);
        throw error;
    }
};

//getting member by id
const fetchMemberById = async (memberId: number): Promise<Homeless | null> => {
    try {
        const response = await SamaritanFetch.v3(`/samaritan_members/${memberId}`, {
            query: { template: 'small' }
        });
        if (response && response.ok) {
            const data = await response.json();
            return data.samaritan_member || null;
        }
        return null;
    } catch (error) {
        console.error(`Error fetching member ${memberId}:`, error);
        return null;
    }
};

//getting members for current page
const fetchMembersForPage = async (caseloads: Caseload[], page: number, itemsPerPage: number): Promise<Homeless[]> => {
    try {
        // Calculate which caseloads are on this page
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const pagesCaseloads = caseloads.slice(startIndex, endIndex);

        // Fetch only the members for this page
        const memberPromises = pagesCaseloads.map((caseload) => 
            fetchMemberById(caseload.homeless_id)
        );

        const members = await Promise.all(memberPromises);
        
        // Filter out null results only
        return members.filter((member): member is Homeless => member !== null);
    } catch (error) {
        console.error('Error fetching members for page:', error);
        throw error;
    }
};

const MyMemberList: React.FC = () => {
    const classes = useStyles();
    const isSmall = useMediaQuery('(max-width: 1278px)');
    
    const [members, setMembers] = useState<Homeless[]>([]);
    const [caseloads, setCaseloads] = useState<Caseload[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isMembersLoading, setIsMembersLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(12); // Same as GroupMemberList

    // Load caseloads only once
    useEffect(() => {
        const loadCaseloads = async () => {
            try {
                setIsLoading(true);
                setError(null);
                
                const caseloadsData = await fetchMemberCaseloads();
                setCaseloads(caseloadsData);
            } catch (err) {
                setError('Failed to load caseloads');
                console.error('Error loading caseloads:', err);
            } finally {
                setIsLoading(false);
            }
        };

        loadCaseloads();
    }, []);

    // Load members for current page when page changes or caseloads are loaded
    useEffect(() => {
        if (caseloads.length === 0) return;

        const loadMembersForPage = async () => {
            try {
                setIsMembersLoading(true);
                setError(null);
                
                const membersData = await fetchMembersForPage(caseloads, currentPage, itemsPerPage);
                setMembers(membersData);
            } catch (err) {
                setError('Failed to load members');
                console.error('Error loading members for page:', err);
            } finally {
                setIsMembersLoading(false);
            }
        };

        loadMembersForPage();
    }, [caseloads, currentPage, itemsPerPage]);

    //skeleton layout
    const SkeletonLayout = () => {
        return (
            <Box sx={{ padding: 2, width: '96%' }}>
                <Grid container spacing={4}>
                    {[...Array(itemsPerPage)].map((_, index) => (
                        <Grid item xs={12} sm={6} lg={4} key={index}>
                            <Skeleton
                                variant="rounded"
                                height={200}
                                animation="wave"
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    };

    //getting pagination info
    const getPaginationInfo = () => {
        const totalItems = caseloads.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        return { totalItems, totalPages };
    };

    //handling page change
    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    };

    //rendering members
    const renderMembers = () => {
        //if no caseloads, return empty message
        if (caseloads.length === 0) {
            return (
                <p className={classes.empty}>
                    No members found
                </p>
            );
        }

        //if members are loading, return skeleton layout
        if (isMembersLoading) {
            return <SkeletonLayout />;
        }

        //rendering members
        return members.map((member) => {
            const memberCaseload = caseloads.find(
                (caseload) => caseload.homeless_id === member.id
            );

            return (
                <Grid
                    key={`member-${member.id}`}
                    xs={12}
                    sm={6}
                    lg={4}
                    sx={{
                        minHeight: '240px',
                        maxHeight: '240px',
                        padding: isSmall
                            ? '8px !important'
                            : '20px !important'
                    }}
                    item
                >
                    <ListItem
                        caseload={memberCaseload!}
                        holder={member}
                        memberCareCoordinatorReferrals={null}
                        setReferCaseload={() => {}}
                        setReferHolder={() => {}}
                        setReferPopup={() => {}}
                    />
                </Grid>
            );
        });
    };

    //if loading (if caseloads are loading), return skeleton layout
    if (isLoading) {
        return <SkeletonLayout />;
    }

    //if error, return error message
    if (error) {
        return (
            <p className={classes.empty}>
                {error}
            </p>
        );
    }

    //rendering members
    return (
        <div className={classes.root}>
            <Grid container>
                {renderMembers()}
            </Grid>
            
            {/* Pagination Controls */}
            {getPaginationInfo().totalPages > 1 && (
                <div className={classes.paginationContainer}>
                    <Pagination
                        count={getPaginationInfo().totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: '#1F2152',
                                '&:hover': {
                                    backgroundColor: '#E5E7EB'
                                },
                                '&.Mui-selected': {
                                    backgroundColor: '#7378E8',
                                    color: '#FFFFFF'
                                }
                            }
                        }}
                        size="medium"
                        showFirstButton={false}
                        showLastButton={false}
                    />
                </div>
            )}
        </div>
    );
};

export default MyMemberList;
